const createRecipeValidator = (model, intl, normative) => {
  const getConcreteCommonReg = (model) =>
    `[H][${
      model === 'delivery' ? 'A' : 'M'
    }]-(20|25|30|35|40|45|50|55|60|70|80|90|100)/(AC|[SPBFL])/(8|12|16|20|22|40)/`;

  const getEHEReg = (model) =>
    new RegExp(
      '^' +
        '(?!\\s)' +
        getConcreteCommonReg(model) +
        '(I|IIa|IIb|IIIa|IIIb|IIIc|IV)(.*)?' +
        '(?<!\\s)' +
        '$',
    );

  const getCEReg = (model) =>
    new RegExp(
      '^' +
        '(?!\\s)' +
        getConcreteCommonReg(model) +
        'X(0|C1|C2|C3|C4|S1|S2|S3|D1|D2|D3|A1|A2|A3|F1|F2|F3|F4|M1|M2|M3)' +
        '(\\+X(0|C1|C2|C3|C4|S1|S2|S3|D1|D2|D3|A1|A2|A3|F1|F2|F3|F4|M1|M2|M3)){0,3}' +
        '(?<!\\s)' +
        '$',
    );
  return (rule, value) => {
    return new Promise((resolve, reject) => {
      try {
        let newValue = String(value || '');
        const projectValue = normative;

        if (!newValue) {
          const requiredMessage = intl.formatMessage({
            id: 'validations.required',
          });
          reject(new Error(requiredMessage));
          return;
        }

        const validationType = model + '_' + projectValue;
        let regex;
        let translationKey;

        switch (validationType) {
          case 'delivery_EHE':
            regex = getEHEReg('delivery');
            translationKey = 'validations.format.delivery_EHE';
            break;
          case 'delivery_CE':
            regex = getCEReg('delivery');
            translationKey = 'validations.format.delivery_CE';
            break;
          case 'deliveryPC_EHE':
            regex = getEHEReg('deliveryPC');
            translationKey = 'validations.format.deliveryPC_EHE';
            break;
          case 'deliveryPC_CE':
            regex = getCEReg('deliveryPC');
            translationKey = 'validations.format.deliveryPC_CE';
            break;
          default:
            const genericMessage = intl.formatMessage({
              id: 'validations.format.recipe.generic',
            });
            reject(new Error(genericMessage));
            return;
        }

        if (!regex.test(newValue)) {
          const errorMessage = intl.formatMessage({ id: translationKey });
          reject(new Error(errorMessage));
        } else {
          resolve();
        }
      } catch (error) {
        console.error(error);
        const genericMessage = intl.formatMessage({
          id: 'validations.format.recipe.generic',
        });
        reject(new Error(genericMessage));
      }
    });
  };
};

const compressiveStrengthValidator = (intl) => (_, value) => {
  // Reglas
  const floatRule = { minValue: 0 };
  const intRule = { required: true, minValue: 1, maxValue: 99 };

  if (!Array.isArray(value) || value.length === 0) {
    return Promise.reject(
      new Error(intl.formatMessage({ id: 'validations.required' })),
    );
  }

  for (let i = 0; i < value.length; i++) {
    const row = value[i];

    const day = row.day;
    if (!day || isNaN(parseInt(day))) {
      return Promise.reject(
        new Error(intl.formatMessage({ id: 'validations.required' })),
      );
    }
    const dayInt = parseInt(day);
    if (dayInt < intRule.minValue || dayInt > intRule.maxValue) {
      return Promise.reject(
        new Error(
          intl.formatMessage({
            id: 'validations.number.between.1.99',
          }),
        ),
      );
    }

    const dayCount = value.filter((r) => String(r.day) === String(day)).length;
    if (dayCount > 1) {
      return Promise.reject(
        new Error(
          intl.formatMessage({
            id: 'validations.duplicate.day',
          }),
        ),
      );
    }

    if (!row.value1 && !row.value2 && !row.value3) {
      return Promise.reject(
        new Error(
          intl.formatMessage({
            id: 'validations.row.required',
          }),
        ),
      );
    }

    for (let j = 1; j <= 3; j++) {
      const v = row[`value${j}`];
      if (v !== '' && v !== undefined && v !== null) {
        const num = parseFloat(v);
        if (isNaN(num) || num < floatRule.minValue) {
          return Promise.reject(
            new Error(
              intl.formatMessage({
                id: 'validations.format.float',
              }),
            ),
          );
        }
      }
    }
  }

  return Promise.resolve();
};

const getFieldsDictionary = (model, intl, normative, isAssayedAndLotified) => {
  return {
    CIF: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.CIF',
      inputType: 'input',
      rules: [
        {
          pattern: /^[A-Z]\d{8}$/,
          message: intl.formatMessage({ id: 'validations.format.cifSimple' }),
        },
      ],
      manualValidation: model === 'delivery',
      lockDisabled: isAssayedAndLotified,
    },

    provider: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.provider',
      inputType: 'input',
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'validations.required' }),
        },
        {
          max: 50,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 50 },
          ),
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    docket_number: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.docket_number',
      inputType: 'input',
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'validations.required' }),
        },
        {
          pattern: /^\d+$/,
          message: intl.formatMessage({ id: 'validations.format.integer' }),
        },
        {
          max: 15,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 15 },
          ),
        },
      ],
      manualValidation: isAssayedAndLotified ? false : true,
      disabled: isAssayedAndLotified,
      lockDisabled: isAssayedAndLotified,
    },

    plant: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.plant',
      inputType: 'input',
      rules: [
        {
          required: model === 'delivery',
          message: intl.formatMessage({ id: 'validations.required' }),
        },
        {
          max: 30,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 30 },
          ),
        },
        {
          validator: (_, value) => {
            if (typeof value === 'string' && value !== value.trim()) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validations.format.plant',
                  }),
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ],
      manualValidation: isAssayedAndLotified ? false : model === 'delivery',
      disabled: isAssayedAndLotified,
      lockDisabled: isAssayedAndLotified,
    },

    arriving_date: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.arriving_date',
      inputType: 'datePicker',
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'validations.required' }),
        },
      ],
      manualValidation: isAssayedAndLotified
        ? false
        : model.includes('delivery'),
      disabled: isAssayedAndLotified,
      lockDisabled: isAssayedAndLotified,
    },

    recipe: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.recipe',
      inputType: 'input',
      rules: [
        {
          max: 30,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 30 },
          ),
        },
        { validator: createRecipeValidator(model, intl, normative) },
      ],
      manualValidation: isAssayedAndLotified
        ? false
        : model.includes('delivery'),
      disabled: isAssayedAndLotified,
      lockDisabled: isAssayedAndLotified,
    },

    delivered: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.delivered',
      inputType: 'input',
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'validations.required' }),
        },
        {
          pattern: /^\d+(\.\d{1,2})?$/,
          message: intl.formatMessage({ id: 'validations.format.decimal' }),
        },
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            const num = parseFloat(value);
            if (num < 1 || num > 10) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({ id: 'validations.number.between.1.10' }),
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ],
      manualValidation: isAssayedAndLotified
        ? false
        : model.includes('delivery'),
      disabled: isAssayedAndLotified,
      lockDisabled: isAssayedAndLotified,
    },

    manufacture_time: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.manufacture_time',
      inputType: 'timePicker',
      rules: [
        {
          required: model === 'delivery',
          message: intl.formatMessage({ id: 'validations.required' }),
        },
      ],
      customValidator: 'validateManufactureTime',
      manualValidation: model === 'delivery',
      lockDisabled: isAssayedAndLotified,
    },

    concrete_id: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.concrete_id',
      inputType: 'input',
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'validations.required' }),
        },
        {
          pattern: /^(CEM.?)\s*(I|II|III|IV|V)(.*\S)?$/,
          message: intl.formatMessage({ id: 'validations.format.concrete_id' }),
        },
        {
          max: 30,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 30 },
          ),
        },
      ],
      manualValidation: model.includes('delivery'),
      lockDisabled: isAssayedAndLotified,
    },

    limit_time: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.limit_time',
      inputType: 'timePicker',
      disabled: true,
      manualValidation: false,
      lockDisabled: true,
    },

    reference_number: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.reference_number',
      inputType: 'input',
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'validations.required' }),
        },
        {
          whitespace: true,
          message: intl.formatMessage({ id: 'validations.no.whitespace.only' }),
        },
        {
          max: 30,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 30 },
          ),
        },
      ],
      manualValidation: model === 'laboratory',
      lockDisabled: isAssayedAndLotified,
    },

    compressive_strength: {
      labelKey:
        'pages.quality.ocr.scannedDocuments.create.compressive_strength',
      inputType: 'compressiveStrengthInput',
      rules: [
        {
          required: true,
          message: intl.formatMessage({ id: 'validations.required' }),
        },
        {
          validator: compressiveStrengthValidator(intl),
        },
      ],
      manualValidation: model === 'laboratory',
      lockDisabled: isAssayedAndLotified,
    },

    compound: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.compound',
      inputType: 'input',
      rules: [
        {
          pattern: /^\d+$/,
          message: intl.formatMessage({ id: 'validations.format.integer' }),
        },
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            const num = parseInt(value);
            if (num < 199 || num > 501) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validations.number.between.199.501',
                  }),
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    relation_wc: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.relation_wc',
      inputType: 'input',
      rules: [
        {
          pattern: /^\d+(\.\d+)?$/,
          message: intl.formatMessage({ id: 'validations.format.float' }),
        },
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            const num = parseFloat(value);
            if (num < 0 || num > 0.65) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validations.number.between.0.0.65',
                  }),
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    admixtures_id: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.admixtures_id',
      inputType: 'input',
      rules: [
        {
          max: 60,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 60 },
          ),
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    admixtures_quantity: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.admixtures_quantity',
      inputType: 'input',
      rules: [
        {
          pattern: /^\d+(\.\d+)?$/,
          message: intl.formatMessage({ id: 'validations.format.float' }),
        },
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();
            const num = parseFloat(value);
            if (num < 0 || num > 999) {
              return Promise.reject(
                new Error(
                  intl.formatMessage({
                    id: 'validations.number.between.0.999',
                  }),
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ],
      customValidator: 'validateFloatField',
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    admixtures_extra: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.admixtures_extra',
      inputType: 'textArea',
      rules: [
        {
          max: 30,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 30 },
          ),
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    delivery_company: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.delivery_company',
      inputType: 'input',
      rules: [
        {
          max: 100,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 100 },
          ),
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    truck: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.truck',
      inputType: 'input',
      rules: [
        {
          pattern: /^(\d{4}-[A-Z]{3}|\d{4}[A-Z]{3})$/,
          message: intl.formatMessage({ id: 'validations.format.plate' }),
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    driver: {
      labelKey: 'pages.quality.ocr.scannedDocuments.create.driver',
      inputType: 'input',
      rules: [
        {
          max: 30,
          message: intl.formatMessage(
            { id: 'validations.number.maxDigits' },
            { max: 30 },
          ),
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },

    delivery_company_cif: {
      labelKey:
        'pages.quality.ocr.scannedDocuments.create.delivery_company_cif',
      inputType: 'input',
      rules: [
        {
          pattern: /^[A-Z]\d{8}$/,
          message: intl.formatMessage({ id: 'validations.format.cifSimple' }),
        },
      ],
      manualValidation: false,
      lockDisabled: isAssayedAndLotified,
    },
  };
};

export default getFieldsDictionary;
